import { Button, TextBlock } from 'bench-internal-theme';
import React from 'react';
import styled from 'styled-components';

import { useGeneralLedger } from './contexts';

const getCurrentNavItemIndex = (firstVisibleListItemIndex, navItems) => {
  if (typeof firstVisibleListItemIndex !== 'number') {
    return undefined;
  }
  return navItems.findIndex((navItem, navItemIndex) => {
    const listItemIndex = navItem.itemProps.index;
    const isThisTheLastNavItem = navItemIndex === navItems.length - 1;
    const nextNavItem = navItems[navItemIndex + 1];
    const nextNavItemListItemIndex = nextNavItem && nextNavItem.itemProps.index;
    return (
      listItemIndex <= firstVisibleListItemIndex &&
      (isThisTheLastNavItem || firstVisibleListItemIndex < nextNavItemListItemIndex)
    );
  });
};

const LedgerNavigationLayout = styled.div`
  display: grid;
  grid-template-rows: 64px auto min-content;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
`;

const LedgerNavigationHeader = styled(TextBlock).attrs(() => ({ styleType: 'title' }))`
  padding: 16px;
`;

const LedgerNavigationLinks = styled.div`
  overflow: auto;
`;

const LedgerNavigationControls = styled.div`
  display: grid;
  grid-template-columns: 120px 120px;
  grid-column-gap: 16px;
  justify-content: center;
  padding: 16px;
`;

const LedgerNavigation = () => {
  const {
    firstVisibleIndex,
    itemData: generalLedgerListItemData,
    scrollToItem,
  } = useGeneralLedger();

  if (!generalLedgerListItemData || !scrollToItem) {
    return null;
  }

  const navItems = generalLedgerListItemData.filter(({ NavComponent }) => NavComponent);
  const currentNavItemIndex = getCurrentNavItemIndex(firstVisibleIndex, navItems);
  const scrollBy = changeToIndex => {
    if (typeof currentNavItemIndex !== 'number') {
      return;
    }
    const navItem = navItems[currentNavItemIndex + changeToIndex];
    if (!navItem) {
      return;
    }
    scrollToItem(Number(navItem.itemProps.index));
  };
  return (
    <LedgerNavigationLayout>
      <LedgerNavigationHeader>Ledgers &amp; Accounts</LedgerNavigationHeader>
      <LedgerNavigationLinks>
        {navItems.map(({ key, NavComponent, navProps }, navItemIndex) => (
          <NavComponent key={key} isCurrent={navItemIndex === currentNavItemIndex} {...navProps} />
        ))}
      </LedgerNavigationLinks>
      <LedgerNavigationControls>
        <Button
          data-heap-id="general-ledger-navigation-previous"
          disabled={currentNavItemIndex === 0}
          onClick={() => scrollBy(-1)}
          styleType="secondary"
        >
          ↑ Previous
        </Button>
        <Button
          data-heap-id="general-ledger-navigation-next"
          disabled={currentNavItemIndex === navItems.length - 1}
          onClick={() => scrollBy(1)}
          styleType="primary"
        >
          ↓ Next
        </Button>
      </LedgerNavigationControls>
    </LedgerNavigationLayout>
  );
};

export default React.memo(LedgerNavigation);
